<script setup lang="ts">
import { EDeviceActions } from '@/store/deviceStore/deviceStoreTypes'
import { EStoreModules } from '@/store/storeType'
import type { DropZoneData } from '@/types/components/DropZoneData'
import type { DeviceModel } from '@/types/models/DeviceModel'
import type { EventModel } from '@/types/models/EventModel'
import DeviceDropZone from '@/views/EventsView/Detail/CheckpointsTable/components/DeviceDropZone.vue'
import { StackLayout, TypographyItem } from '@rfc/rfc-components-library'
import { isMobile, isMobileEvent } from '@rfc/rfc-components-library/utils'
import type { Position } from '@vueuse/core'
import moment from 'moment'
import { ref, watch } from 'vue'
import { useStore } from 'vuex'
import DeviceDragCard from './Card/DeviceDragCard.vue'

const store = useStore()
type Props = {
    event: EventModel
    dropZoneData?: DropZoneData
}
const props = defineProps<Props>()
type Emits = {
    (e: 'dragStart', device?: DeviceModel): void
    (e: 'dragEnd', position?: Position): void
    (e: 'close'): void
    (e: 'refresh'): void
}
const emit = defineEmits<Emits>()

const devices = ref<DeviceModel[]>([])

const getDevices = () =>
    store
        ?.dispatch(`${EStoreModules.DEVICE}/${EDeviceActions.GET_AVAILABLE_DEVICES}`, {
            start_at: moment(props.event?.start_at).utc(true).toISOString(),
            end_at: moment(props.event?.end_at).utc(true).toISOString()
        })
        .then(res => (devices.value = res))

watch(
    () => props.event,
    newEvent => (newEvent ? getDevices() : undefined),
    { deep: true }
)

const handleRefresh = () => {
    getDevices()
    emit('refresh')
}

const showMobileFilters = ref(isMobile())
isMobileEvent(e => (showMobileFilters.value = e.matches))
</script>

<template>
    <StackLayout class="section-box">
        <DeviceDropZone
            :dropZoneData="dropZoneData"
            @close="$emit('close')"
            @refresh="handleRefresh"
            class="overflow-y-auto"
            v-slot="props">
            <StackLayout class="overflow-y-hidden w-full h-full" direction="column" v-bind="props">
                <StackLayout class="section-header align-center py-5">
                    <TypographyItem
                        :label="$t('event.detail.devices.devicesTitle')"
                        class="section-text" />
                </StackLayout>
                <StackLayout
                    v-if="devices?.length"
                    class="overflow-y-scroll device-drawer-content px-6 py-4"
                    direction="column"
                    gap="16">
                    <DeviceDragCard
                        v-for="(device, key) in devices"
                        :key="key"
                        :device="device"
                        @dragStart="$emit('dragStart', $event)"
                        @dragEnd="$emit('dragEnd', $event)" />
                </StackLayout>
                <TypographyItem
                    v-else
                    :label="$t('event.checkpoint.noDeviceAssociated')"
                    class="text-center content-center h-full" />
            </StackLayout>
        </DeviceDropZone>
    </StackLayout>
</template>

<style scoped lang="scss">
.card {
    border-radius: 30px;
    box-shadow: 1px 1px 7px $grey-shadow;

    &:hover {
        box-shadow: 1px 1px 7px rgb(252, 127, 3);
        transition: ease-in-out 0.2s;
    }
    & .inner {
        background: var(--color-background-secondary);
        color: #fff;
        border-radius: 30px;
    }
}
</style>
